import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { api } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Wallet, TrendingUp, TrendingDown, Minus, ArrowRight, Info } from 'lucide-react';

// ★ 2026-08-04 (Fase 34) — Card Dashboard: totale Proposal Payments del mese
// corrente vs mese precedente + delta (assoluto e %).
// Alimenta la sezione Dashboard subito dopo Collections/Proposte pipeline.

// Nome del mese localizzato (1-12) secondo la lingua attiva.
const monthName = (m) => {
  if (!m) return '';
  try {
    const locale = (i18n.language || 'it').split('-')[0];
    const name = new Date(2000, m - 1, 1).toLocaleDateString(locale, { month: 'long' });
    return name.charAt(0).toUpperCase() + name.slice(1);
  } catch { return ''; }
};

const fmtEur = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const fmtEurDetail = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export function MonthlyPaymentsCard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get('/kpis/proposal-payments-monthly')
      .then((r) => { if (mounted) setData(r.data); })
      .catch(() => { if (mounted) setData(null); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-white" data-testid="monthly-payments-card">
        <CardContent className="p-4">
          <Skeleton className="h-6 w-40 mb-3" />
          <Skeleton className="h-10 w-56 mb-2" />
          <Skeleton className="h-4 w-32" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const current = data.current_month || {};
  const previous = data.previous_month || {};
  const deltaAbs = data.delta_absolute_eur ?? 0;
  const deltaPct = data.delta_percentage;
  const positive = deltaAbs > 0;
  const negative = deltaAbs < 0;
  const flat = deltaAbs === 0;
  const monthLabel = monthName(current.month);
  const prevMonthLabel = monthName(previous.month);

  return (
    <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-white" data-testid="monthly-payments-card">
      <CardContent className="p-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          {/* Left: totale mese corrente */}
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-emerald-700">
              <Wallet className="w-4 h-4" />
              <span>{t('payments.monthIncome', 'Incassi mese')} · {monthLabel} {current.year || ''}</span>
              {!data.is_admin && (
                <Badge variant="outline" className="text-[9px] py-0 border-emerald-300 text-emerald-700">
                  {t('payments.mine', 'I miei')}
                </Badge>
              )}
            </div>
            <div className="font-serif text-3xl mt-1 text-emerald-900" data-testid="monthly-payments-total">
              {fmtEur(current.total_amount_eur)}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              {current.count || 0} {current.count === 1 ? t('payments.oneRecorded', 'pagamento registrato') : t('payments.manyRecorded', 'pagamenti registrati')}
            </div>
          </div>

          {/* Right: delta vs mese precedente */}
          <div className="flex flex-col items-end min-w-[190px]">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-slate-500">
              <span>vs {prevMonthLabel} {previous.year || ''}</span>
            </div>
            <div className="flex items-center gap-1 mt-1" data-testid="monthly-payments-delta">
              {positive && <TrendingUp className="w-4 h-4 text-emerald-600" />}
              {negative && <TrendingDown className="w-4 h-4 text-red-600" />}
              {flat && <Minus className="w-4 h-4 text-slate-400" />}
              <span className={`text-lg font-semibold ${positive ? 'text-emerald-700' : negative ? 'text-red-700' : 'text-slate-500'}`}>
                {positive ? '+' : ''}{fmtEur(deltaAbs)}
              </span>
            </div>
            <div className={`text-[11px] mt-0.5 ${positive ? 'text-emerald-700' : negative ? 'text-red-700' : 'text-slate-500'}`}>
              {deltaPct === null || deltaPct === undefined ? (
                <span className="italic text-slate-500">{t('payments.noBenchmark', 'Nessun benchmark')}</span>
              ) : (
                <>
                  {positive ? '+' : ''}{deltaPct?.toFixed?.(1)}% {t('payments.vsPrevMonth', 'rispetto al mese precedente')}
                </>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              {prevMonthLabel}: {fmtEurDetail(previous.total_amount_eur)}
            </div>
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-emerald-100 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-1 text-[11px] text-emerald-800/80">
            <Info className="w-3 h-3" />
            <span>{t('payments.sumHint', 'Somma dei pagamenti registrati alla chiusura delle proposte (Proposal Payments).')}</span>
          </div>
          <Link
            to="/app/payments"
            className="text-[11px] text-emerald-700 hover:text-emerald-900 flex items-center gap-1"
            data-testid="monthly-payments-detail-link"
          >
            Dettaglio pagamenti <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

export default MonthlyPaymentsCard;
